/**
 * /memory — "What Jesus remembers about you" page.
 *
 * Inline-editable facts, pause-memory toggle, and big delete-all button.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Trash2, Plus, X } from "lucide-react";

import AuthNav from "@/components/AuthNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  getMe, getMemory, patchMemory, deleteMemory, patchMe,
} from "@/lib/api";

export default function Memory() {
  const navigate = useNavigate();
  const [authReady, setAuthReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [facts, setFacts] = useState([]);
  const [consent, setConsent] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newVal, setNewVal] = useState("");
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    const me = await getMe();
    const mem = await getMemory();
    if (!me.body || me.body.scope !== "user") {
      navigate("/", { replace: true });
      return;
    }
    setConsent(Boolean(mem.body && mem.body.memory_consent));
    setFacts((mem.body && mem.body.facts) || []);
    setAuthReady(true);
    setLoading(false);
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const addFact = async () => {
    const k = newKey.trim();
    const v = newVal.trim();
    if (!k || !v || saving || !consent) return;
    setSaving(true);
    await patchMemory({ add: [{ key: k, value: v }] });
    setNewKey("");
    setNewVal("");
    await refresh();
    setSaving(false);
  };

  const removeFact = async (key) => {
    await patchMemory({ remove_keys: [key] });
    setFacts((prev) => prev.filter((f) => f.key !== key));
  };

  const toggleConsent = async () => {
    const next = !consent;
    setConsent(next);
    await patchMe({ memory_consent: next });
  };

  const wipeAll = async () => {
    if (!window.confirm("Forget everything? This can't be undone.")) return;
    await deleteMemory();
    setFacts([]);
  };

  if (!authReady || loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]" data-testid="memory-page">
      <AuthNav />
      <main className="mx-auto max-w-2xl px-4 py-8 sm:px-6">
        <header className="mb-6">
          <h1 className="font-serif text-3xl tracking-tight" data-testid="memory-heading">
            What Jesus remembers about you
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            You're in control of what stays.
          </p>
        </header>

        {/* Consent toggle */}
        <section className="mb-6 flex items-center justify-between rounded-2xl border border-border/60 bg-[hsl(var(--card))] px-5 py-4">
          <div>
            <p className="text-sm font-medium text-foreground">Memory</p>
            <p className="text-xs text-muted-foreground">
              {consent
                ? "On — Jesus may quietly remember helpful details across calls."
                : "Paused — nothing new will be stored or referenced."}
            </p>
          </div>
          <Button
            data-testid="memory-consent-toggle"
            variant={consent ? "default" : "outline"}
            onClick={toggleConsent}
            className="rounded-full"
          >
            {consent ? "Pause memory" : "Enable memory"}
          </Button>
        </section>

        {/* Facts list */}
        {facts.length === 0 ? (
          <div
            data-testid="memory-empty"
            className="rounded-3xl border border-border/60 bg-[hsl(var(--card))] px-6 py-10 text-center"
          >
            <p className="font-serif italic text-muted-foreground">
              Memory begins to form after your conversations. You're in control of what stays.
            </p>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="memory-facts">
            {facts.map((f) => (
              <li
                key={f.key}
                data-testid={`memory-fact-${f.key}`}
                className="flex items-start justify-between gap-3 rounded-2xl border border-border/60 bg-[hsl(var(--card))] px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{f.key}</p>
                  <p className="mt-0.5 text-sm text-foreground/90">{f.value}</p>
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => removeFact(f.key)}
                  data-testid={`memory-remove-${f.key}`}
                  aria-label={`Forget ${f.key}`}
                >
                  <X className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}

        {/* Add fact */}
        {consent && (
          <div className="mt-5 flex flex-col gap-2 rounded-2xl border border-dashed border-border/70 px-4 py-4 sm:flex-row">
            <Input
              data-testid="memory-new-key"
              placeholder="Key (e.g. prayer_for)"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              className="sm:w-44"
            />
            <Input
              data-testid="memory-new-value"
              placeholder="Value"
              value={newVal}
              onChange={(e) => setNewVal(e.target.value)}
              className="flex-1"
            />
            <Button
              data-testid="memory-add"
              onClick={addFact}
              disabled={!newKey.trim() || !newVal.trim() || saving}
              className="rounded-full"
            >
              <Plus className="mr-1.5 h-4 w-4" />
              Add
            </Button>
          </div>
        )}

        {/* Delete all */}
        {facts.length > 0 && (
          <div className="mt-8 flex justify-end">
            <Button
              variant="ghost"
              onClick={wipeAll}
              data-testid="memory-wipe-all"
              className="text-destructive"
            >
              <Trash2 className="mr-1.5 h-4 w-4" />
              Forget everything
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
